import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router";
import { GraphQlAdapter } from "@src/infrastructure/adapters/graphQl/GraphQl.adapter";
import {
  assertGraphQLMutationResult,
  withMutationTimeout,
} from "@src/infrastructure/adapters/graphQl/graphqlMutationUtils";
import { GET_USER_DETAILS, GET_USERS } from "@src/domain/graphql";
import { UPDATE_USER_DETAILS } from "@src/domain/graphql/updateUserDetails";
import { AdminUser, UserDetails } from "@src/domain/entities/auth/user";
import type { UserDetailsBrand } from "@src/domain/entities/auth/user/UserDetails";
import { UpdateUserBrandDTO, type UpdateUserBrandDTOShape, type IUpdateUserProps } from "@src/domain/dto/users";
import { UserRepository } from "@src/infrastructure/repositories/user";
import { UpdateUserBrandUseCase } from "@src/application/useCases/user";

interface UsersAccessPageCacheEntry {
  users: AdminUser[];
  total: number;
  totalActiveCount: number;
  page: number;
  limit: number;
}

function normalizeSearch(search: string): string {
  return search.trim().toLowerCase();
}

function toAdminUserFromUpdateProps(
  props: IUpdateUserProps,
  existing?: AdminUser,
): AdminUser {
  return new AdminUser({
    sub: props.authId,
    email: props.email,
    firstName: props.firstName,
    lastName: props.lastName,
    avatar: existing?.avatar ?? null,
    adminRoles: existing?.adminRoles ?? [],
    adminPermissions: existing?.adminPermissions ?? [],
    adminAccessToken: existing?.adminAccessToken ?? null,
    adminRefreshToken: existing?.adminRefreshToken ?? null,
    adminContext: {
      type:
        props.kind ||
        existing?.kind ||
        existing?.adminContext.type ||
        "app_user",
      brand_id: existing?.adminContext.brand_id ?? null,
      is_global_admin: existing?.adminContext.is_global_admin ?? false,
    },
    userId: props.userId,
    isActive: props.isActive,
    isBot: props.isBot,
    kind: props.kind,
    isDeleted: props.isDeleted ?? existing?.isDeleted ?? false,
    organizations: props.organizations ?? existing?.organizations ?? [],
  });
}

/** Cache key for a single page + search: "page-limit-search". */
function usersAccessPageCacheKey(
  page: number,
  limit: number,
  search: string,
): string {
  return `${page}-${limit}-${normalizeSearch(search)}`;
}

/**
 * Persistent cache survives provider unmount/remount.
 * This is what prevents loader flicker when revisiting the page.
 */
const persistentUsersAccessPageCache = new Map<
  string,
  UsersAccessPageCacheEntry
>();
const optimisticallyCreatedUserKeys = new Set<string>();

function getAdminUserIdentityKeys(user: AdminUser): string[] {
  const keys: string[] = [];
  if (user.userId != null) {
    keys.push(`id:${user.userId}`);
  }
  if (user.sub?.trim()) {
    keys.push(`sub:${user.sub}`);
  }
  return keys;
}

function hasAnyAdminUserIdentity(
  user: AdminUser,
  identities: ReadonlySet<string>,
): boolean {
  return getAdminUserIdentityKeys(user).some((key) => identities.has(key));
}

function uniqueUsersByIdentity(users: AdminUser[]): AdminUser[] {
  const seen = new Set<string>();
  return users.filter((user) => {
    const keys = getAdminUserIdentityKeys(user);
    const duplicate = keys.some((key) => seen.has(key));
    keys.forEach((key) => seen.add(key));
    return !duplicate;
  });
}

function removeOptimisticCreatedUsersFromPage(
  users: AdminUser[],
  targetPage: number,
  targetSearch: string,
): AdminUser[] {
  const shouldFilterOptimisticCreated =
    targetPage > 1 &&
    normalizeSearch(targetSearch).length === 0 &&
    optimisticallyCreatedUserKeys.size > 0;

  const filtered = shouldFilterOptimisticCreated
    ? users.filter(
        (user) => !hasAnyAdminUserIdentity(user, optimisticallyCreatedUserKeys),
      )
    : users;

  return uniqueUsersByIdentity(filtered);
}

interface UsersAccessOrganizationResponse {
  organizationId: number;
  organizationName: string;
}

interface UsersAccessItemResponse {
  userId: number;
  authId: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  isBot: boolean;
  kind: string;
  isDeleted: boolean;
  organizations: UsersAccessOrganizationResponse[];
}

interface UsersAccessApiResponse {
  users: {
    total: number;
    page: number;
    limit: number;
    totalActiveCount: number;
    items: UsersAccessItemResponse[];
  };
}

interface UserDetailsOrganizationResponse {
  organizationId: number;
  organizationName: string;
  isActive: boolean;
  organizationLogoUrl: string | null;
}

interface UserDetailsRoleResponse {
  roleId: number;
  roleName: string;
  roleKey: string;
}

interface UserDetailsPermissionResponse {
  permissionId: number;
  permissionName: string;
  permissionKey: string;
  isInherited: boolean;
  isCustom: boolean;
  isAssigned: boolean;
}

interface UserDetailsFeatureResponse {
  featureId: number;
  featureName: string;
  featureKey: string;
  isMainMenu: boolean;
  permissions: UserDetailsPermissionResponse[];
}

interface UserDetailsBrandResponse {
  brandId: number;
  brandName: string;
  brandLogoUrl: string | null;
  organizationId: number;
  organizationName: string;
  brandUserMappingId: number;
  isAccessActive: boolean;
  roles: UserDetailsRoleResponse[];
  features: UserDetailsFeatureResponse[];
}

interface UserDetailsResponse {
  userDetails: {
    userId: number;
    authId: string;
    firstName: string;
    lastName: string;
    email: string;
    isActive: boolean;
    isBot: boolean;
    kind: string;
    createdAt: string;
    deletedAt: string | null;
    organizations: UserDetailsOrganizationResponse[];
    brands: UserDetailsBrandResponse[];
  } | null;
}

interface UpdateUserDetailsPermissionResponse {
  brandUserPermissionId: number;
  permissionId: number;
  permissionName: string;
  permissionKey: string;
  isInherited: boolean;
  isCustom: boolean;
  isAssigned: boolean;
}

interface UpdateUserDetailsRoleResponse {
  roleId: number;
  roleName: string;
  roleKey: string;
  updatedAt: string;
  updatedBy: number;
}

interface UpdateUserDetailsDataResponse {
  brandUserId: number;
  brandId: number;
  isActive: boolean;
  updatedAt: string;
  role: UpdateUserDetailsRoleResponse;
  customPermissions: UpdateUserDetailsPermissionResponse[];
}

interface UpdateUserDetailsResponse {
  updateUserDetails: UpdateUserDetailsDataResponse[];
}

export type UpdateUserBrandInput = UpdateUserBrandDTOShape;

export interface UpdateUserDetailsBrandInput {
  brandUserId: number;
  roleId?: number;
  customPermissions?: number[];
  isActive?: boolean;
}

export interface UpdateUserDetailsInput {
  brands: UpdateUserDetailsBrandInput[];
}

export interface UsersAccessDataContextValue {
  users: AdminUser[];
  total: number;
  totalActiveCount: number;
  page: number;
  limit: number;
  search: string;
  loading: boolean;
  error: string | null;
  userDetailsLoading: boolean;
  userDetailsError: string | null;
  userDetails: UserDetails | null;
  getUserDetails: (
    userId: number,
    forceRefresh?: boolean,
  ) => Promise<UserDetails | null>;
  updateUserDetailsLoading: boolean;
  updateUserDetails: (
    params: UpdateUserDetailsInput,
  ) => Promise<UpdateUserDetailsDataResponse[]>;
  updateUserBrandLoading: boolean;
  updateUserBrand: (params: UpdateUserBrandInput) => Promise<UserDetailsBrand>;
  /** Prepends a newly assigned brand to user details without refetching. */
  applyUserBrandAdd: (brand: UserDetailsBrand) => void;
  /** Updates a brand's access status (and optional role) in user details cache. */
  applyUserBrandAccessUpdate: (
    brandId: number,
    updates: {
      isAccessActive: boolean;
      role?: UserDetailsBrand["roles"][number];
    },
  ) => void;
  /**
   * Forces the users list to be re-fetched from the API.
   * Useful after create/update actions to avoid serving stale cached data.
   */
  refreshUsersList: () => void;
  /** Prepends a newly created user to the current list without a full refetch. */
  applyUserCreate: (created: IUpdateUserProps) => void;
  /** Updates an existing user in list and details cache without a full refetch. */
  applyUserUpdate: (updated: IUpdateUserProps) => void;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setLimit: React.Dispatch<React.SetStateAction<number>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
}

const UsersAccessDataContext =
  createContext<UsersAccessDataContextValue | null>(null);

export function useUsersAccessData(): UsersAccessDataContextValue {
  const value = useContext(UsersAccessDataContext);
  if (value === null) {
    throw new Error(
      "useUsersAccessData must be used within a UsersAccessDataProvider",
    );
  }
  return value;
}

function mapUsersAccessItemToAdminUser(
  item: UsersAccessItemResponse,
): AdminUser {
  return new AdminUser({
    sub: item.authId,
    email: item.email,
    firstName: item.firstName,
    lastName: item.lastName,
    avatar: null,
    adminRoles: [],
    adminPermissions: [],
    adminAccessToken: null,
    adminRefreshToken: null,
    adminContext: {
      type: item.kind,
      brand_id: null,
      is_global_admin: false,
    },
    userId: item.userId,
    isActive: item.isActive,
    isBot: item.isBot,
    kind: item.kind,
    isDeleted: item.isDeleted,
    organizations: item.organizations,
  });
}

const userDetailsCache = new Map<number, UserDetails>();

function mapUserDetailsResponseToEntity(
  response: NonNullable<UserDetailsResponse["userDetails"]>,
): UserDetails {
  return new UserDetails({
    userId: response.userId,
    authId: response.authId,
    firstName: response.firstName,
    lastName: response.lastName,
    email: response.email,
    isActive: response.isActive,
    isBot: response.isBot,
    kind: response.kind,
    createdAt: response.createdAt,
    deletedAt: response.deletedAt,
    organizations: response.organizations ?? [],
    brands: response.brands ?? [],
  });
}

interface UsersAccessDataProviderProps {
  children: ReactNode;
  defaultPageNumber?: number;
  defaultUsersPageSize?: number;
}

export function UsersAccessDataProvider({
  children,
  defaultPageNumber = 1,
  defaultUsersPageSize = 15,
}: Readonly<UsersAccessDataProviderProps>) {
  const location = useLocation();
  const [search, setSearch] = useState<string>("");
  const shouldLoadUsersList = useMemo(
    () => location.pathname === "/users-access",
    [location.pathname],
  );
  const initialCacheKey = usersAccessPageCacheKey(
    defaultPageNumber,
    defaultUsersPageSize,
    "",
  );
  const initialCached = persistentUsersAccessPageCache.get(initialCacheKey);

  const [users, setUsers] = useState<AdminUser[]>(
    () => initialCached?.users ?? [],
  );
  const [total, setTotal] = useState<number>(() => initialCached?.total ?? 0);
  const [totalActiveCount, setTotalActiveCount] = useState<number>(
    () => initialCached?.totalActiveCount ?? 0,
  );
  const [page, setPage] = useState<number>(
    () => initialCached?.page ?? defaultPageNumber,
  );
  const [limit, setLimit] = useState<number>(
    () => initialCached?.limit ?? defaultUsersPageSize,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forceUsersListRefresh, setForceUsersListRefresh] = useState(false);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [userDetailsError, setUserDetailsError] = useState<string | null>(null);
  const [updateUserDetailsLoading, setUpdateUserDetailsLoading] =
    useState(false);
  const [updateUserBrandLoading, setUpdateUserBrandLoading] = useState(false);

  useEffect(() => {
    if (!shouldLoadUsersList) {
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    async function fetchUsersAccess(): Promise<void> {
      const cacheKey = usersAccessPageCacheKey(page, limit, search);
      const cached = persistentUsersAccessPageCache.get(cacheKey);

      if (cached && !forceUsersListRefresh) {
        if (cancelled) return;
        const cachedUsers = removeOptimisticCreatedUsersFromPage(
          cached.users,
          cached.page,
          search,
        );
        setUsers(cachedUsers);
        setTotal(cached.total);
        setTotalActiveCount(cached.totalActiveCount ?? 0);
        setPage(cached.page);
        setLimit(cached.limit);
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const result = await GraphQlAdapter.getInstance()
          .getClient()
          .query<UsersAccessApiResponse>({
            query: GET_USERS,
            variables: {
              page_number: page,
              page_size: limit,
              search: normalizeSearch(search) ? search.trim() : null,
            },
          });

        if (cancelled) return;

        const response = result.data?.users;
        if (!response) {
          throw new Error("Users lookup response is empty");
        }

        const responseUsers = response.items.map(mapUsersAccessItemToAdminUser);
        const nextTotalActiveCount = response.totalActiveCount ?? 0;
        const nextPage = response.page ?? page;
        const nextLimit = limit;
        const nextUsers = removeOptimisticCreatedUsersFromPage(
          responseUsers,
          nextPage,
          search,
        );
        setUsers(nextUsers);
        setTotal(response.total ?? 0);
        setTotalActiveCount(nextTotalActiveCount);
        setPage(nextPage);
        setLimit(nextLimit);

        persistentUsersAccessPageCache.set(cacheKey, {
          users: nextUsers,
          total: response.total ?? 0,
          totalActiveCount: nextTotalActiveCount,
          page: nextPage,
          limit: nextLimit,
        });
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : "Failed to load users";
        setError(message);
        setUsers([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setForceUsersListRefresh(false);
        }
      }
    }

    fetchUsersAccess();
    return () => {
      cancelled = true;
    };
  }, [page, limit, search, shouldLoadUsersList, forceUsersListRefresh]);

  const refreshUsersList = useCallback(() => {
    setForceUsersListRefresh(true);
  }, []);

  const syncUsersPageCache = useCallback(
    (nextUsers: AdminUser[], nextTotal: number, nextActiveCount: number) => {
      const cacheKey = usersAccessPageCacheKey(page, limit, search);
      persistentUsersAccessPageCache.set(cacheKey, {
        users: nextUsers,
        total: nextTotal,
        totalActiveCount: nextActiveCount,
        page,
        limit,
      });
    },
    [page, limit, search],
  );

  const applyUserCreate = useCallback(
    (created: IUpdateUserProps) => {
      const adminUser = toAdminUserFromUpdateProps(created);
      getAdminUserIdentityKeys(adminUser).forEach((key) =>
        optimisticallyCreatedUserKeys.add(key),
      );
      setUsers((prev) => {
        const alreadyListed = prev.some(
          (user) =>
            user.userId === adminUser.userId ||
            (user.sub && adminUser.sub && user.sub === adminUser.sub),
        );
        if (alreadyListed) return prev;

        const onFirstPageWithoutSearch =
          page === 1 && normalizeSearch(search).length === 0;
        let nextUsers = prev;
        if (onFirstPageWithoutSearch) {
          nextUsers = [adminUser, ...prev];
          if (nextUsers.length > limit) {
            nextUsers = nextUsers.slice(0, limit);
          }
        }

        const nextTotal = total + 1;
        const nextActiveCount =
          adminUser.isActive && !adminUser.isDeleted
            ? totalActiveCount + 1
            : totalActiveCount;
        setTotal(nextTotal);
        setTotalActiveCount(nextActiveCount);
        syncUsersPageCache(nextUsers, nextTotal, nextActiveCount);
        persistentUsersAccessPageCache.forEach((entry, cacheKey) => {
          const isCurrentPage =
            cacheKey === usersAccessPageCacheKey(page, limit, search);
          const isUnsearchedPage =
            cacheKey === usersAccessPageCacheKey(entry.page, entry.limit, "");
          if (isCurrentPage || !isUnsearchedPage) return;

          persistentUsersAccessPageCache.set(cacheKey, {
            ...entry,
            users: removeOptimisticCreatedUsersFromPage(
              entry.users,
              entry.page,
              "",
            ),
            total: nextTotal,
            totalActiveCount: nextActiveCount,
          });
        });
        return nextUsers;
      });
    },
    [limit, page, search, syncUsersPageCache, total, totalActiveCount],
  );

  const applyUserUpdate = useCallback(
    (updated: IUpdateUserProps) => {
      const replaceUser = (existing: AdminUser): AdminUser =>
        toAdminUserFromUpdateProps(updated, existing);

      setUsers((prev) => {
        const existing = prev.find((user) => user.userId === updated.userId);
        let nextActiveCount = totalActiveCount;

        if (existing && !existing.isDeleted) {
          const wasActive = Boolean(existing.isActive);
          const isActive = Boolean(updated.isActive);
          if (wasActive && !isActive) {
            nextActiveCount = Math.max(0, totalActiveCount - 1);
          } else if (!wasActive && isActive) {
            nextActiveCount = totalActiveCount + 1;
          }
        }

        const nextUsers = prev.map((user) =>
          user.userId === updated.userId ? replaceUser(user) : user,
        );

        if (nextActiveCount !== totalActiveCount) {
          setTotalActiveCount(nextActiveCount);
        }
        syncUsersPageCache(nextUsers, total, nextActiveCount);
        return nextUsers;
      });

      setUserDetails((prev) => {
        if (prev?.userId !== updated.userId) return prev;
        const next = new UserDetails({
          userId: updated.userId,
          authId: updated.authId || prev.authId,
          firstName: updated.firstName,
          lastName: updated.lastName,
          email: updated.email,
          isActive: updated.isActive,
          isBot: updated.isBot,
          kind: updated.kind || prev.kind,
          createdAt: prev.createdAt,
          deletedAt: prev.deletedAt,
          organizations: prev.organizations,
          brands: prev.brands,
        });
        userDetailsCache.set(updated.userId, next);
        return next;
      });
    },
    [syncUsersPageCache, total, totalActiveCount],
  );

  const getUserDetails = useCallback(
    async (
      userId: number,
      forceRefresh = false,
    ): Promise<UserDetails | null> => {
      if (forceRefresh) {
        userDetailsCache.delete(userId);
      } else {
        const cached = userDetailsCache.get(userId);
        if (cached) {
          setUserDetails(cached);
          setUserDetailsError(null);
          return cached;
        }
      }

      setUserDetailsLoading(true);
      setUserDetailsError(null);
      try {
        const result = await GraphQlAdapter.getInstance()
          .getClient()
          .query<UserDetailsResponse>({
            query: GET_USER_DETAILS,
            variables: { userId },
          });
        const payload = result.data?.userDetails;
        if (!payload) {
          setUserDetails(null);
          return null;
        }
        const entity = mapUserDetailsResponseToEntity(payload);
        userDetailsCache.set(userId, entity);
        setUserDetails(entity);
        return entity;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to load user details";
        setUserDetailsError(message);
        setUserDetails(null);
        return null;
      } finally {
        setUserDetailsLoading(false);
      }
    },
    [],
  );

  const updateUserDetails = useCallback(
    async (
      params: UpdateUserDetailsInput,
    ): Promise<UpdateUserDetailsDataResponse[]> => {
      setUpdateUserDetailsLoading(true);
      try {
        const result = await withMutationTimeout(
          GraphQlAdapter.getInstance()
            .getClient()
            .mutate<UpdateUserDetailsResponse>({
              mutation: UPDATE_USER_DETAILS,
              variables: {
                params,
              },
              errorPolicy: "none",
            }),
        );

        assertGraphQLMutationResult(result, "Failed to update user details");

        const payload = result.data.updateUserDetails;
        if (!payload?.length) {
          throw new Error("Failed to update user details");
        }
        return payload;
      } finally {
        setUpdateUserDetailsLoading(false);
      }
    },
    [],
  );

  const updateUserBrand = useCallback(
    async (params: UpdateUserBrandInput): Promise<UserDetailsBrand> => {
      const validated = UpdateUserBrandDTO.validate(params);
      if (!validated.success) {
        throw new Error("Invalid brand assignment data");
      }

      setUpdateUserBrandLoading(true);
      try {
        const dto = new UpdateUserBrandDTO(validated.data);
        return await new UpdateUserBrandUseCase(new UserRepository()).execute(
          dto,
        );
      } finally {
        setUpdateUserBrandLoading(false);
      }
    },
    [],
  );

  const applyUserBrandAdd = useCallback((brand: UserDetailsBrand) => {
    setUserDetails((prev) => {
      if (!prev) return prev;
      const alreadyAssigned = prev.brands.some(
        (existing) => existing.brandId === brand.brandId,
      );
      if (alreadyAssigned) return prev;

      const next = new UserDetails({
        userId: prev.userId,
        authId: prev.authId,
        firstName: prev.firstName,
        lastName: prev.lastName,
        email: prev.email,
        isActive: prev.isActive,
        isBot: prev.isBot,
        kind: prev.kind,
        createdAt: prev.createdAt,
        deletedAt: prev.deletedAt,
        organizations: prev.organizations,
        brands: [brand, ...prev.brands],
      });
      userDetailsCache.set(prev.userId, next);
      return next;
    });
  }, []);

  const applyUserBrandAccessUpdate = useCallback(
    (
      brandId: number,
      updates: {
        isAccessActive: boolean;
        role?: UserDetailsBrand["roles"][number];
      },
    ) => {
      setUserDetails((prev) => {
        if (!prev) return prev;

        const nextBrands = prev.brands.map((brand) => {
          if (brand.brandId !== brandId) return brand;
          return {
            ...brand,
            isAccessActive: updates.isAccessActive,
            roles: updates.role ? [updates.role] : brand.roles,
          };
        });

        const next = new UserDetails({
          userId: prev.userId,
          authId: prev.authId,
          firstName: prev.firstName,
          lastName: prev.lastName,
          email: prev.email,
          isActive: prev.isActive,
          isBot: prev.isBot,
          kind: prev.kind,
          createdAt: prev.createdAt,
          deletedAt: prev.deletedAt,
          organizations: prev.organizations,
          brands: nextBrands,
        });
        userDetailsCache.set(prev.userId, next);
        return next;
      });
    },
    [],
  );

  const value = useMemo<UsersAccessDataContextValue>(
    () => ({
      users,
      total,
      totalActiveCount,
      page,
      limit,
      search,
      loading,
      error,
      userDetailsLoading,
      userDetailsError,
      userDetails,
      getUserDetails,
      updateUserDetailsLoading,
      updateUserDetails,
      updateUserBrandLoading,
      updateUserBrand,
      applyUserBrandAdd,
      applyUserBrandAccessUpdate,
      refreshUsersList,
      applyUserCreate,
      applyUserUpdate,
      setPage,
      setLimit,
      setSearch,
    }),
    [
      users,
      total,
      totalActiveCount,
      page,
      limit,
      search,
      loading,
      error,
      userDetailsLoading,
      userDetailsError,
      userDetails,
      getUserDetails,
      updateUserDetailsLoading,
      updateUserDetails,
      updateUserBrandLoading,
      updateUserBrand,
      applyUserBrandAdd,
      applyUserBrandAccessUpdate,
      refreshUsersList,
      applyUserCreate,
      applyUserUpdate,
    ],
  );

  return (
    <UsersAccessDataContext.Provider value={value}>
      {children}
    </UsersAccessDataContext.Provider>
  );
}
